// ─────────────────────────────────────────────────────────────
// PoReportModal.jsx — Report 2 filter window
// Picks vendor / status / date range, fetches, prints.
// ─────────────────────────────────────────────────────────────

import React, { useState } from 'react';
import axios from 'axios';
import WindowModal from '../../components/WindowModal';
import PrintButton from '../../print/PrintButton';
import { poReportConfig } from './poReport.config';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const DEFAULT_FROM_DATE = '2025-01-01';
const today = () => new Date().toISOString().split('T')[0];

const C = {
  text: '#1a2332',
  textSoft: '#64748b',
  textMuted: '#94a3b8',
  border: '#e2e8f0',
};

const inputStyle = {
  width: '100%', padding: '10px 13px',
  border: `1px solid ${C.border}`, borderRadius: '8px',
  fontSize: '14px', color: C.text, boxSizing: 'border-box',
  fontFamily: 'inherit', background: '#ffffff',
};

const Field = ({ label, children }) => (
  <div>
    <label style={{
      display: 'block', fontSize: '12px', fontWeight: '600',
      color: C.textSoft, marginBottom: '6px',
    }}>
      {label}
    </label>
    {children}
  </div>
);

const PoReportModal = ({ vendors, onClose }) => {
  const [vendorId, setVendorId] = useState('');
  const [status, setStatus] = useState('');
  const [fromDate, setFromDate] = useState(DEFAULT_FROM_DATE);
  const [toDate, setToDate] = useState(today());
  const [error, setError] = useState('');

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const buildConfig = async () => {
    setError('');
    const params = new URLSearchParams();
    if (vendorId) params.append('vendor_id', vendorId);
    if (status) params.append('status', status);
    params.append('from_date', fromDate);
    params.append('to_date', toDate);

    const res = await axios.get(
      `${API}/tape/purchase-orders?${params.toString()}`,
      { headers }
    );
    const orders = res.data.data || [];

    if (orders.length === 0) {
      setError('No purchase orders match these filters.');
      return null;
    }

    const vendor = vendors.find((v) => String(v.id) === String(vendorId));

    return poReportConfig(orders, {
      vendor_name: vendor ? vendor.name : '',
      status,
      from_date: fromDate,
      to_date: toDate,
    });
  };

  return (
    <WindowModal
      id="po-report"
      title="Purchase Order Report"
      subtitle="Filter the orders, then print or download."
      onClose={onClose}
      width="620px"
    >
      <div style={{ padding: '22px 24px' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px', marginBottom: '20px',
        }}>
          <Field label="Vendor">
            <select
              style={inputStyle}
              value={vendorId}
              onChange={(e) => setVendorId(e.target.value)}
            >
              <option value="">All vendors</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))}
            </select>
          </Field>

          <Field label="Status">
            <select
              style={inputStyle}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">All status</option>
              <option value="pending">Pending</option>
              <option value="partial">Partial</option>
              <option value="received">Received</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </Field>

          <Field label="From date">
            <input
              type="date"
              style={inputStyle}
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </Field>

          <Field label="To date">
            <input
              type="date"
              style={inputStyle}
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </Field>
        </div>

        {error && (
          <div style={{ fontSize: '13px', color: '#dc2626', marginBottom: '14px' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <PrintButton
            label="Download PDF"
            filename={`PO-Report-${fromDate}-to-${toDate}`}
            getConfig={buildConfig}
          />
          <PrintButton
            label="Print"
            mode="print"
            icon="🖨"
            getConfig={buildConfig}
          />
          <span style={{ fontSize: '12px', color: C.textMuted, marginLeft: 'auto' }}>
            Vendor blank = all vendors
          </span>
        </div>
      </div>
    </WindowModal>
  );
};

export default PoReportModal;